import React, { useEffect, useRef, useState } from 'react';
import { fmtCount, fmtPct, fmtSigned } from '../../utils/formatters';
import {
  GLYPH_TITLE, OutlookDay, callChip, dayGlyph, dayLabel, dollarScale, lowF, signedMoneyRange, weekSentence,
} from '../../utils/weather';
import DayDetail from './DayDetail';
import DeviationBar from './DeviationBar';
import WeatherGlyph from './WeatherGlyph';
import { Chip, ChipTone, FOCUS, MICRO, NOTE, TD, TD_NUM, TH, TH_NUM, TableToggle } from './weatherUi';

// The week ahead as a strip: seven columns from md up, a list of rows under
// that. Every day carries a deviation bar on one dollar scale for the week, so
// a big day looks big; the bar is the part that must survive at 360px.
// Days 4 to 7 are muted with a Possible chip (the likely/possible rule, drawn).

const DETAIL_ID = 'weather-day-detail';
const moved = (d: OutlookDay) => d.call === 'slower' || d.call === 'busier';
const crosses = (d: OutlookDay) => (d.dollars_low ?? 0) < 0 && (d.dollars_high ?? 0) > 0;

function chipTone(d: OutlookDay): ChipTone {
  if (d.closed) return 'quiet';
  if (d.call === 'slower') return 'slower';
  if (d.call === 'busier') return 'busier';
  return d.call === 'holiday' ? 'plum' : 'quiet';
}

/** A day's dollars in words beside its bar: the point, signed. */
const barWords = (d: OutlookDay) => (moved(d) ? fmtSigned(d.dollars ?? ((d.dollars_low ?? 0) + (d.dollars_high ?? 0)) / 2) : '');

/** Rain in the rush: words on a phone, a small track in the desktop column. */
function RushRain({ chance }: { chance: number }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {/* A track only when the chance is worth a look; below 20% the number alone. */}
      {chance >= 0.2 && (
        <span data-track aria-hidden="true" className="hidden h-1 w-8 overflow-hidden rounded-full bg-[#E6E2DE] md:block">
          <span className="block h-full rounded-full bg-[#6B6670]" style={{ width: `${Math.min(100, chance * 100)}%` }} />
        </span>
      )}
      <span className="tabular-nums"><span className="md:sr-only">rush rain </span>{fmtPct(chance * 100)}</span>
    </span>
  );
}

function DayCell({ d, scale, open, onToggle }: { d: OutlookDay; scale: number; open: boolean; onToggle: () => void }) {
  const muted = d.horizon >= 4 || d.label === 'possible';
  const kind = dayGlyph(d);
  const low = lowF(d);
  const ink = muted ? 'text-[#6B6670]' : 'text-[#2D2A2E]';
  // One set of elements, two layouts: a three-column grid row on a phone
  // (glyph | day, weather, chips | bar), a stacked column from md up.
  return (
    <li data-day={d.date} data-muted={String(muted)} className="min-w-0">
      <button
        type="button" aria-expanded={open} aria-controls={DETAIL_ID} onClick={onToggle}
        className={`grid w-full grid-cols-[auto_minmax(0,1fr)_38%] items-center gap-x-3 gap-y-1 rounded-[10px] px-2 py-3 text-left transition-colors md:flex md:h-full md:flex-col md:items-stretch md:gap-2 md:px-2.5 ${open ? 'bg-[#F0EBF5]' : 'hover:bg-[#FAF9F7]'} ${FOCUS}`}
      >
        <span className={`col-start-2 row-start-1 block font-sans text-sm font-medium ${ink}`}>{dayLabel(d.date)}</span>
        <span className={`col-start-1 row-span-3 row-start-1 ${muted ? 'text-[#7C7589]' : 'text-[#4A2D5E]'}`}>
          {kind ? (
            <>
              <WeatherGlyph kind={kind} size={22} />
              <span className="sr-only">{GLYPH_TITLE[kind]}</span>
            </>
          ) : <span aria-hidden="true" className="block h-[22px] w-[22px]" />}
        </span>
        <span className={`${NOTE} col-start-2 row-start-2 flex flex-wrap items-center gap-x-1.5 md:flex-col md:items-start md:gap-1`}>
          {d.weather ? (
            <>
              <span className={`tabular-nums md:text-sm ${ink}`}>
                {fmtCount(d.weather.high_f)}°{low != null && <span className="text-[#6B6670]"> / {fmtCount(low)}°</span>}
              </span>
              <RushRain chance={d.weather.rain_am_chance} />
            </>
          ) : !d.closed && <span>No forecast</span>}
        </span>
        <span className="col-start-2 row-start-3 flex flex-wrap gap-1">
          <Chip tone={chipTone(d)} kind="call">{callChip(d)}</Chip>
          {muted && !d.closed && <Chip tone="quiet" kind="possible">Possible</Chip>}
        </span>
        <span className="col-start-3 row-span-3 row-start-1 md:mt-auto md:w-full">
          {moved(d) ? (
            <DeviationBar point={d.dollars ?? 0} low={d.dollars_low ?? 0} high={d.dollars_high ?? 0} scale={scale} muted={muted} />
          ) : (
            <span aria-hidden="true" className="relative block h-4"><span className="absolute inset-y-0 left-1/2 w-px bg-[#E6E2DE]" /></span>
          )}
          <span className={`${MICRO} mt-0.5 block text-center tabular-nums ${moved(d) ? 'text-[#2D2A2E]' : ''}`}>
            {moved(d) ? barWords(d) : '\u00a0'}
          </span>
        </span>
      </button>
    </li>
  );
}

export default function WeekStrip({ days }: { days: OutlookDay[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const scale = dollarScale(days);
  const selected = days.find((d) => d.date === open);
  // On a phone the detail opens below seven rows: bring it into view.
  const detail = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = detail.current;
    if (open && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [open]);
  return (
    <div>
      <div aria-hidden="true" className="mb-1 flex justify-end md:hidden">
        <div className={`${MICRO} flex w-[38%] justify-between`}>
          <span>slower</span><span>usual</span><span>busier</span>
        </div>
      </div>
      <ol aria-label={weekSentence(days)} className="divide-y divide-[#E6E2DE] md:grid md:grid-cols-7 md:gap-1 md:divide-y-0">
        {days.map((d) => (
          <DayCell key={d.date} d={d} scale={scale} open={open === d.date} onToggle={() => setOpen(open === d.date ? null : d.date)} />
        ))}
      </ol>
      <div className={`${NOTE} mt-3 flex flex-wrap items-center gap-x-4 gap-y-1`}>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2 w-3 rounded-l-[4px] bg-[#B54A4A]" />slower</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2 w-3 rounded-r-[4px] bg-[#2E6B4F]" />busier</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2 w-px bg-[#6B6670]" />a usual day</span>
        <span>Bars share one dollar scale; the thin line is the range. Tap a day for the math.</span>
      </div>
      <div ref={detail}>{selected && <DayDetail day={selected} id={DETAIL_ID} />}</div>
      <TableToggle className="mt-3">
        <table className="w-full font-sans text-xs">
          <caption className="sr-only">The week ahead, day by day</caption>
          <thead>
            <tr className="border-b border-[#E6E2DE]">
              <th scope="col" className={TH}>Day</th>
              <th scope="col" className={TH}>Call</th>
              <th scope="col" className={TH_NUM}>High / low</th>
              <th scope="col" className={TH_NUM}>Rush rain</th>
              <th scope="col" className={TH_NUM}>Against a usual day</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6E2DE]">
            {days.map((d) => {
              const low = lowF(d);
              return (
                <tr key={d.date}>
                  <td className={TD}>{dayLabel(d.date)}</td>
                  <td className={TD}>{callChip(d)}{d.label === 'possible' && !d.closed ? ' (possible)' : ''}</td>
                  <td className={TD_NUM}>{d.weather ? `${fmtCount(d.weather.high_f)}°${low != null ? ` / ${fmtCount(low)}°` : ''}` : ''}</td>
                  <td className={TD_NUM}>{d.weather ? fmtPct(d.weather.rain_am_chance * 100) : ''}</td>
                  <td className={TD_NUM}>
                    {moved(d) && !crosses(d) ? signedMoneyRange(d.dollars_low ?? 0, d.dollars_high ?? 0) : moved(d) ? barWords(d) : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableToggle>
    </div>
  );
}
